/**
 * Demo mode — what the client sees ON THE WEBSITE.
 *
 * SCOPE: this is a WEB HOSTING concern only. The website runs as two Netlify
 * deployments from two branches:
 *
 *   demo  →  the client's URL.  Curated: only finished work is reachable.
 *   main  →  our URL.           Everything, including half-built screens.
 *
 * The MOBILE APP is not part of this. It is never hosted on Netlify; it ships
 * through EAS to TestFlight and Play internal testing, where showing a build to
 * someone is a matter of which channel they are on, not which branch was
 * deployed. Do not import this module from apps/mobile.
 *
 * The branch decides WHICH COMMIT is deployed; this flag decides WHAT IS
 * VISIBLE in it. Both are needed.
 *
 * Branch alone is not enough: to hide an unfinished feature you would have to
 * withhold the whole commit, which also withholds every finished thing built
 * alongside it. Within weeks the demo is months stale and bringing one feature
 * forward means cherry-picking and resolving conflicts.
 *
 * With this flag the demo branch can sit on a recent, fully working commit and
 * still show only what is ready. Nothing is ever cherry-picked, so the two
 * branches never diverge and there is never a merge conflict between them.
 *
 * Set VITE_DEMO_MODE=true in the Netlify environment of the client-facing site
 * only.
 *
 * WHAT THIS IS NOT: it is not a security boundary. It hides navigation, not
 * data. Anyone who types a URL directly still reaches the route, and the
 * database is protected by row-level security, never by this. Do not use it to
 * conceal anything that must actually stay private.
 */

type MaybeEnv = Record<string, string | boolean | undefined> | undefined

/**
 * Reads the flag from whichever bundler is present.
 *
 * Vite exposes `import.meta.env`; Node exposes `process.env`. Both are read
 * defensively because referencing the missing one throws rather than returning
 * undefined, and this module is loaded by two Vite apps plus the odd script.
 */
function readEnv(key: string): string | undefined {
  try {
    const meta = (import.meta as unknown as { env?: MaybeEnv }).env
    const fromMeta = meta?.[key]
    if (fromMeta !== undefined) return String(fromMeta)
  } catch {
    // import.meta is not available in this runtime; fall through.
  }

  // Reached through globalThis rather than the bare `process` identifier: this
  // package is compiled without Node types (it is shared with the browser and
  // React Native), so naming `process` directly is a type error even inside a
  // typeof guard.
  const globals = globalThis as { process?: { env?: Record<string, string | undefined> } }
  const fromProcess = globals.process?.env?.[key]
  if (fromProcess !== undefined) return String(fromProcess)

  return undefined
}

function isTruthy(value: string | undefined): boolean {
  if (!value) return false
  const v = value.trim().toLowerCase()
  return v === 'true' || v === '1' || v === 'yes' || v === 'on'
}

/** True on the client-facing website deployment. False everywhere else, including local. */
export function isDemoMode(): boolean {
  return isTruthy(readEnv('VITE_DEMO_MODE'))
}

/**
 * Should a feature be visible here?
 *
 * `built` says whether the feature is finished. In demo mode only finished
 * features are shown; everywhere else everything is, because we need to reach
 * the half-built screens in order to build them.
 */
export function isVisible(built: boolean): boolean {
  return built || !isDemoMode()
}

/**
 * Filters a list of navigable things down to what this deployment should show.
 *
 *   const visible = filterForDeployment(MODULES, (m) => m.built)
 */
export function filterForDeployment<T>(items: readonly T[], isBuilt: (item: T) => boolean): T[] {
  if (!isDemoMode()) return [...items]
  return items.filter(isBuilt)
}

/**
 * A label for the environment badge, so nobody demonstrates the wrong build to
 * a client, or reports a bug against the demo thinking it was the dev site.
 */
export function deploymentLabel(): 'demo' | 'development' {
  return isDemoMode() ? 'demo' : 'development'
}
